import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot } from 'lucide-react';
import { API_BASE_URL } from '../lib/awsConfig';

const SUGGESTED = [
  'How does the Claim Robot work?',
  'Does it work with WaveOrtho?',
  'How do you handle Cigna denials?',
  'How much does it cost?',
  'Is it HIPAA compliant?',
];

export default function DemoChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm here to answer any questions about FlowRCM while you explore. What would you like to know?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg || loading) return;
    setInput('');

    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      // Skip the initial greeting when sending to API
      const apiMessages = newMessages.slice(1);
      const res = await fetch(`${API_BASE_URL}/demo-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply || 'Something went wrong — feel free to call us!'
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Connection issue. Still have questions? You can always call us after the demo!'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Ask about FlowRCM"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          width: 52, height: 52, borderRadius: '50%',
          background: 'linear-gradient(135deg, #0f766e, #14b8a6)',
          border: 'none', color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          transition: 'transform 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 88, right: 24, zIndex: 9998,
          width: 340, height: 460,
          background: '#fff', borderRadius: 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.07)',
        }}>

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #0f766e, #14b8a6)',
            padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          }}>
            <Bot size={20} color="#fff" />
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>FlowRCM Assistant</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>Ask me anything about the product</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '82%',
                  background: m.role === 'user' ? 'linear-gradient(135deg, #0f766e, #14b8a6)' : '#f1f5f9',
                  color: m.role === 'user' ? '#fff' : '#1e293b',
                  padding: '9px 12px', borderRadius: 12, fontSize: 13, lineHeight: 1.5,
                  borderBottomRightRadius: m.role === 'user' ? 4 : 12,
                  borderBottomLeftRadius: m.role === 'assistant' ? 4 : 12,
                  whiteSpace: 'pre-wrap',
                }}>
                  {m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  background: '#f1f5f9', padding: '9px 14px', borderRadius: 12,
                  borderBottomLeftRadius: 4, fontSize: 13, color: '#64748b',
                }}>
                  Thinking...
                </div>
              </div>
            )}

            {messages.length >= 5 && !loading && (
              <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Still have questions? You can always call us after the demo.
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Suggested questions — only shown before first user message */}
          {messages.length === 1 && (
            <div style={{ padding: '0 12px 10px', display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
              {SUGGESTED.map(q => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                    padding: '6px 10px', fontSize: 12, color: '#0f766e',
                    textAlign: 'left', cursor: 'pointer', fontWeight: 500,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input bar */}
          <div style={{
            padding: '10px 12px', borderTop: '1px solid #f1f5f9',
            display: 'flex', gap: 8, flexShrink: 0,
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              placeholder="Ask a question..."
              disabled={loading}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 8,
                border: '1px solid #e2e8f0', fontSize: 13, outline: 'none',
                background: loading ? '#f8fafc' : '#fff', color: '#1e293b',
              }}
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              style={{
                background: input.trim() && !loading ? 'linear-gradient(135deg, #0f766e, #14b8a6)' : '#e2e8f0',
                border: 'none', borderRadius: 8, padding: '8px 12px',
                color: input.trim() && !loading ? '#fff' : '#94a3b8',
                cursor: input.trim() && !loading ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', flexShrink: 0,
              }}
            >
              <Send size={15} />
            </button>
          </div>

        </div>
      )}
    </>
  );
}
